export type Grants = Record<string, boolean | undefined>;

export type PermissionTypeRow = {
  key: string;
  category: string;
  label?: string;
  desc?: string;
  metadata?: { sub_category?: string; required_feature_flags?: string[]; warning?: string };
};

export type PermissionType = {
  key: string;
  category: string;
  label: string;
  desc: string;
  subCategory: string;
  requiredFlags: string[];
  warning: string | null;
};

export type PermissionGroup = { name: string; permissions: PermissionType[] };
export type PermissionSection = { name: string; groups: PermissionGroup[] };

const SECTION_ORDER = [
  "Data",
  "Sandbox",
  "General",
  "Metrics",
  "Calendar",
  "Players",
  "Health",
  "Manual Changes",
  "Flags & Reports",
  "Import Data",
  "Team Access",
];

// Sub-categories sort by name, except in the sections listed here.
const GROUP_ORDER: Record<string, string[]> = {
  Data: ["GPS", "RPE", "Wellness", "Force Plate", "Testing", "Nutrition", "Cognition"],
};

const VERB_RANK = new Map([
  ["view", 0],
  ["access", 0],
  ["add", 1],
  ["create", 1],
  ["edit", 2],
  ["override", 2],
  ["manage", 2],
  ["delete", 3],
]);
const NO_VERB_RANK = 4;

// A permission row that no backfill has touched keeps the model defaults: an empty label and empty metadata.
export const toPermissionType = (row: PermissionTypeRow): PermissionType => ({
  key: row.key,
  category: row.category,
  label: row.label || row.key,
  desc: row.desc ?? "",
  subCategory: row.metadata?.sub_category || row.category,
  requiredFlags: row.metadata?.required_feature_flags ?? [],
  warning: row.metadata?.warning ?? null,
});

export const isGranted = (grants: Grants, key: string) => grants[key] === true;

export const isActive = (permission: PermissionType, teamInfo: Record<string, unknown>) =>
  permission.requiredFlags.every((flag) => teamInfo[flag] === true);

const verbOf = (label: string) => label.toLowerCase().split(/[^a-z]+/).find((word) => VERB_RANK.has(word));

const verbRank = (label: string) => VERB_RANK.get(verbOf(label) ?? "") ?? NO_VERB_RANK;

const byVerb = (a: PermissionType, b: PermissionType) =>
  verbRank(a.label) - verbRank(b.label) || a.label.localeCompare(b.label);

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

// A name that is not in the order list goes after every listed name.
const rankIn = (order: string[], name: string) => {
  const index = order.indexOf(name);
  return index === -1 ? order.length : index;
};

const byOrder = (order: string[]) => (a: { name: string }, b: { name: string }) =>
  rankIn(order, a.name) - rankIn(order, b.name) || byName(a, b);

export const groupPermissions = (permissions: PermissionType[]): PermissionSection[] => {
  const sections = new Map<string, Map<string, PermissionType[]>>();
  permissions.forEach((permission) => {
    const groups = sections.get(permission.category) ?? new Map<string, PermissionType[]>();
    groups.set(permission.subCategory, [...(groups.get(permission.subCategory) ?? []), permission]);
    sections.set(permission.category, groups);
  });
  return [...sections]
    .map(([name, groups]) => ({
      name,
      groups: [...groups]
        .map(([groupName, rows]) => ({ name: groupName, permissions: rows.sort(byVerb) }))
        .sort(byOrder(GROUP_ORDER[name] ?? [])),
    }))
    .sort(byOrder(SECTION_ORDER));
};

export const matchesSearch = (permission: PermissionType, query: string) => {
  const needle = query.trim().toLowerCase();
  return [permission.label, permission.desc, permission.subCategory, permission.category].some((text) =>
    text.toLowerCase().includes(needle)
  );
};

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The matches of the query sit at the odd indexes of the result.
export const splitOnMatches = (text: string, query: string) => {
  const needle = query.trim();
  return needle ? text.split(new RegExp(`(${escapeRegExp(needle)})`, "gi")) : [text];
};

const groupSummary = (group: PermissionGroup, grants: Grants) => {
  const granted = group.permissions.filter((permission) => isGranted(grants, permission.key)).length;
  return granted ? `${group.name} (${granted})` : "";
};

export const sectionSummary = (section: PermissionSection, grants: Grants) => {
  const permissions = section.groups.flatMap((group) => group.permissions);
  const granted = permissions.filter((permission) => isGranted(grants, permission.key)).length;
  if (!granted) return "No access";
  if (granted === permissions.length) return "Everything in this section";
  return section.groups
    .map((group) => groupSummary(group, grants))
    .filter(Boolean)
    .join(", ");
};

export const changedKeys = (permissions: PermissionType[], saved: Grants, draft: Grants) =>
  permissions
    .filter((permission) => isGranted(saved, permission.key) !== isGranted(draft, permission.key))
    .map((permission) => permission.key);

export const changeSummary = (changed: string[], draft: Grants) => {
  const added = changed.filter((key) => isGranted(draft, key)).length;
  const removed = changed.length - added;
  const parts = [added ? `${added} added` : "", removed ? `${removed} removed` : ""].filter(Boolean);
  return parts.length ? `Unsaved changes: ${parts.join(", ")}` : "All changes saved";
};
